import { useEffect, useRef, useState } from 'react';
import { deleteMedia, getImage, getMediaKeys, saveImage } from '../helpers/indexedDbHelper';

// Los <video> se piden por rangos (HTTP 206) y la Cache API no puede guardar respuestas
// parciales, asi que el service worker nunca alcanza a cachearlos y sin internet el video
// queda en negro. Guardamos el mp4 como blob en IndexedDB y lo reproducimos con un blob:
// URL, que es local y no depende de la red ni del service worker.

const VIDEO_PREFIX = 'video-';

// '/static/media/banner1.efd8ee3c9ecc74aba9ea.mp4' -> '/static/media/banner1.mp4'
const baseName = (url) => url.replace(/\.[0-9a-f]{8,}\.(mp4|webm|mov)$/i, '.$1');

// Borra las versiones viejas del mismo video (cada build cambia el hash del archivo).
const removeOldVersions = async (id, url) => {
  try {
    const base = baseName(url);
    const keys = await getMediaKeys();
    for (const key of keys) {
      if (typeof key === 'string' && key.startsWith(VIDEO_PREFIX) && key !== id) {
        if (baseName(key.slice(VIDEO_PREFIX.length)) === base) {
          await deleteMedia(key);
        }
      }
    }
  } catch (error) {
    console.log('Error limpiando videos viejos:', error);
  }
};

export const useVideoBlob = (url) => {
  const [src, setSrc] = useState(null);
  const objectUrl = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const id = `${VIDEO_PREFIX}${url}`;

    const publish = (blob) => {
      if (cancelled) return;
      objectUrl.current = URL.createObjectURL(blob);
      setSrc(objectUrl.current);
    };

    const load = async () => {
      try {
        const stored = await getImage(id);
        if (stored && stored.data && stored.data.blob) {
          publish(stored.data.blob);
          return;
        }
      } catch (error) {
        console.log('Error leyendo el video de IndexedDB:', error);
      }

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        await saveImage(id, { blob });
        await removeOldVersions(id, url);
        publish(blob);
      } catch (error) {
        // Sin internet y sin copia local: al menos intentamos con la URL directa.
        console.log('No se pudo descargar el video, se usa la URL directa:', error);
        if (!cancelled) setSrc(url);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl.current) {
        URL.revokeObjectURL(objectUrl.current);
        objectUrl.current = null;
      }
    };
  }, [url]);

  return src;
};
